<template>
    <!-- eslint-disable vue/no-unused-vars -->
    <EventCard
        class="event-date-card"
        :class="{
            'event-card--cancelled': event.isCancelled,
            'event-card--with-pricing': withPricing
        }"
        element="div"
        :appearance
    >
        <template #before>
            <a
                :href="`/programma/${event?.uri}`"
                class="event-date-card__link"
                @click.prevent.stop="showEventDetail"
            />
        </template>

        <template #image>
            <BaseImage
                v-if="featuredImage"
                class="base-image--cover"
                :src="imageSrc"
                :srcset="imageSrcset"
                :webp-srcset="imageWebpSrcset"
                :width="featuredImage.width || 270"
                :height="featuredImage.height || 337"
                :alt="featuredImage.alt"
            />
        </template>

        <template
            v-if="event.oneLiner"
            #oneLiner
        >
            {{ event.oneLiner }}
        </template>

        <template #disciplines>
            <EventDisciplines :disciplines="event.disciplines" />
        </template>

        <template
            v-if="label"
            #label
        >
            {{ label }}
        </template>

        <template
            v-if="isSaved || isReserved"
            #actions
        >
            <ReservedLabel v-if="isReserved" />
        </template>

        <template #meta>
            <EventMeta
                :event="event"
            >
                <template
                    v-if="!event.hideArtist"
                    #artists
                >
                    <span class="event-meta__item event-meta__artists">
                        {{ artistNames }}
                    </span>
                </template>
            </EventMeta>
        </template>

        <template #after>
            <div
                v-if="useCanShowPricing(event) && withPricing && event.ticketPriceRegular"
                class="event-card__pricing"
            >
                <span class="event-card__pricing__regular">
                    <template v-if="event.ticketPriceRegular">
                        {{ '€' + parseTicketPrice(event.ticketPriceRegular) }}
                    </template>

                    <template v-else>
                        €0
                    </template>
                </span>

                <span class="event-card__pricing__paid">
                    {{ event.paidPrice ? ('€' + parseTicketPrice(event.paidPrice)) : 'Gratis' }}
                </span>
            </div>

            <EventSaveButton
                v-else-if="showSaveButton"
                :event-id="event.id"
                :icon-only="true"
            />
        </template>
    </EventCard>
</template>

<script setup lang="ts">
import { parseTicketPrice } from '~/utils/ticketPricing';

type Props = {
    event: EventDate;
    appearance?: 'box' | 'list';
    withPricing?: boolean;
    showSaveButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    appearance: 'box',
    withPricing: false,
    showSaveButton: true,
});

const { event } = toRefs(props);

const userStore = useUserStore();
const modalStore = useModalStore();
const eventsStore = useEventsStore();

const { savedEvents } = storeToRefs(userStore);

const label = computed(() => {
    if (props.event.programLine) {
        return props.event.programLine.name;
    }

    return null;
});

const artistNames = computed(() => {
    if (!props.event.artists || props.event.artists.length === 0) {
        return '';
    }

    return props.event.artists.map(({ name }) => name).join(', ');
});

const isReserved = computed(() => userStore.hasReservation(props.event.id));

const isSaved = computed(() => savedEvents.value.includes(props.event.id));

const featuredImage = computed(() => props.event?.featuredImage);

const imageSrc = computed(() => {
    if (!featuredImage.value) {
        return '';
    }

    if (props.appearance === 'list') {
        return featuredImage.value.url_list_md;
    }

    return featuredImage.value.url_box_md;
});

const imageSrcset = computed(() => {
    if (!featuredImage.value) {
        return;
    }

    if (props.appearance === 'list') {
        return `
            ${featuredImage.value.url_list_md} 80w,
            ${featuredImage.value.url_list_lg} 2x
        `.trim();
    }

    return `
        ${featuredImage.value.url_box_md} 270w,
        ${featuredImage.value.url_box_lg} 2x
    `.trim();
});

const imageWebpSrcset = computed(() => {
    if (!featuredImage.value) {
        return;
    }

    if (
        props.appearance === 'list' &&
        featuredImage.value.url_list_md_webp &&
        featuredImage.value.url_list_lg_webp
    ) {
        return `
            ${featuredImage.value.url_list_md_webp} 80w,
            ${featuredImage.value.url_list_lg_webp} 2x
        `.trim();
    }

    if (
        featuredImage.value.url_list_md_webp &&
        featuredImage.value.url_list_lg_webp
    ) {
        return `
            ${featuredImage.value.url_box_md_webp} 270w,
            ${featuredImage.value.url_box_lg_webp} 2x
        `.trim();
    }

    return undefined;
});

const showEventDetail = () => {
    // fixme: make the open and close of the event-detail modal life closer together.
    modalStore.open(MODAL_MAIN);
    eventsStore.activate(event.value?.id);

    // push the url of the programme we opened to the history,
    // so the user can share the url or press the refresh button.
    // this will open the programma as a seperate page instead of a modal.
    // nuxt does not see this change.
    window.history.pushState(null, '', `/programma/${event.value?.uri}`);
};
</script>

<style lang="less" src="./EventDateCard.less"></style>
